import React, { useEffect, useState } from "react";
import { db } from "../utils/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "../hooks/AuthenticationProvider";

const InvoiceRow = ({
  id,
  residentId,
  invoiceNumber,
  invoiceWeek,
  fundingSource,
  paymentAmount,
  paid: initialPaid,
  onPaidChange,
  originalPaymentRef,
  residentName: initialResidentName,
}) => {
  const { organizationData, organizationId, checkPermission } = useAuth();
  const [residentName, setResidentName] = useState(initialResidentName || "");
  const [localPaid, setLocalPaid] = useState(initialPaid);

  useEffect(() => {
    // First check if we have a name from the invoice document
    if (initialResidentName) {
      setResidentName(initialResidentName);
      return;
    }

    // If no name in the document, try to find the resident in active residents
    const resident = organizationData?.residents?.find(
      (r) => r.id === residentId
    );

    if (resident) {
      setResidentName(`${resident.firstName} ${resident.lastName}`);
    } else {
      setResidentName("Unknown Resident");
    }
  }, [residentId, organizationData, initialResidentName]);

  // Update localPaid when the prop changes
  useEffect(() => {
    setLocalPaid(initialPaid);
  }, [initialPaid]);

  const handleCheckboxChange = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Set the local state immediately so the UI updates
    const newValue = !localPaid;
    setLocalPaid(newValue);

    try {
      // Query for the document
      const q = query(
        collection(db, `organizations/${organizationId}/invoices`),
        where("invoiceNumber", "==", invoiceNumber),
        where("residentId", "==", residentId),
        where("fundingSource", "==", fundingSource)
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        // If not found, revert the UI change
        setLocalPaid(!newValue);
        alert("Could not find this invoice in the database.");
        return;
      }

      // Update the document
      const matchingDoc = querySnapshot.docs[0];
      await updateDoc(matchingDoc.ref, { paidInvoice: newValue });

      // Notify parent
      onPaidChange(matchingDoc.id, newValue);
    } catch (error) {
      // On error, revert the UI change
      setLocalPaid(!newValue);
      console.error("Error updating invoice status:", error);
      alert(`Failed to update invoice status: ${error.message}`);
    }
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    handleCheckboxChange(e);
  };

  return (
    <tr
      className={`border-b ${
        localPaid ? "border-emerald-800/30" : "border-red-800/30"
      } hover:bg-emerald-800/20 transition-colors duration-200`}
    >
      <td className="px-6 py-4">
        {/* Custom button checkbox for better mobile interaction */}
        <div className="flex items-center justify-center">
          <button
            type="button"
            onClick={handleCheckboxChange}
            onTouchStart={handleTouchStart}
            disabled={!checkPermission("updateInvoices")}
            className={`
              w-6 h-6 
              rounded 
              border-2
              flex items-center justify-center
              transition-all duration-200
              ${
                localPaid
                  ? "border-emerald-500 bg-emerald-500"
                  : "border-gray-400 bg-transparent"
              }
              ${
                !checkPermission("updateInvoices")
                  ? "opacity-50 cursor-not-allowed"
                  : "active:scale-95 hover:scale-105 cursor-pointer"
              }
            `}
            style={{
              WebkitTapHighlightColor: "transparent",
              WebkitTouchCallout: "none",
              WebkitUserSelect: "none",
              userSelect: "none",
            }}
          >
            {localPaid && (
              <svg
                className="w-4 h-4 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>
        </div>
      </td>
      <td className="px-6 py-4 text-emerald-50 font-medium">{residentName}</td>
      <td className="px-6 py-4 text-emerald-50">{invoiceNumber}</td>
      <td className="px-6 py-4 text-emerald-50">{invoiceWeek}</td>
      <td className="px-6 py-4 text-emerald-50">{fundingSource}</td>
      <td className="px-6 py-4 text-emerald-50">${paymentAmount}</td>
      <td className="px-6 py-4">
        <span
          className={`px-2 py-1 text-xs font-semibold rounded-full ${
            localPaid
              ? "bg-emerald-400/20 text-emerald-400"
              : "bg-red-400/20 text-red-400"
          }`}
        >
          {localPaid ? "Paid" : "Unpaid"}
        </span>
      </td>
    </tr>
  );
};

const InvoiceCard = ({
  id,
  residentId,
  invoiceNumber,
  invoiceWeek,
  fundingSource,
  paymentAmount,
  paid,
  onPaidChange,
  originalPaymentRef,
}) => {
  const { organizationData, organizationId, checkPermission } = useAuth();
  const [residentName, setResidentName] = useState("");
  const [localPaid, setLocalPaid] = useState(paid);

  /* keep local state in sync with prop */
  useEffect(() => {
    setLocalPaid(paid);
  }, [paid]);

  /* look up resident name (handles discharged residents gracefully) */
  useEffect(() => {
    const resident = organizationData?.residents?.find(
      (r) => r.id === residentId
    );
    if (resident) {
      setResidentName(`${resident.firstName} ${resident.lastName}`);
    }
  }, [residentId, organizationData]);

  const handleCheckboxChange = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!checkPermission("updateInvoices")) {
      alert("You do not have permission to update invoices.");
      return;
    }

    const newValue = !localPaid;
    setLocalPaid(newValue); // optimistic UI

    try {
      const q = query(
        collection(db, `organizations/${organizationId}/invoices`),
        where("invoiceNumber", "==", invoiceNumber),
        where("residentId", "==", residentId),
        where("fundingSource", "==", fundingSource)
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setLocalPaid(!newValue); // revert
        alert(
          "Could not find this invoice in the database. It may have been deleted."
        );
        return;
      }

      const matchingDoc = querySnapshot.docs[0];
      await updateDoc(matchingDoc.ref, { paidInvoice: newValue });

      onPaidChange(matchingDoc.id, newValue);
    } catch (error) {
      console.error("Error updating invoice status:", error);
      setLocalPaid(!newValue); // revert
      alert(`Failed to update invoice status: ${error.message}`);
    }
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    handleCheckboxChange(e);
  };

  return (
    <div
      className={`rounded-lg p-4 shadow-lg border ${
        localPaid
          ? "bg-emerald-900/30 border-emerald-800"
          : "bg-red-900/30 border-red-800"
      }`}
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          {/* Custom checkbox button for mobile */}
          <button
            type="button"
            onClick={handleCheckboxChange}
            onTouchStart={handleTouchStart}
            disabled={!checkPermission("updateInvoices")}
            className={`
              w-7 h-7 
              rounded 
              border-2
              flex items-center justify-center
              transition-all duration-200
              ${
                localPaid
                  ? "border-emerald-500 bg-emerald-500"
                  : "border-gray-400 bg-transparent"
              }
              ${
                !checkPermission("updateInvoices")
                  ? "opacity-50 cursor-not-allowed"
                  : "active:scale-95 hover:scale-105"
              }
            `}
            style={{
              WebkitTapHighlightColor: "transparent",
              WebkitTouchCallout: "none",
              WebkitUserSelect: "none",
              userSelect: "none",
            }}
          >
            {localPaid && (
              <svg
                className="w-4 h-4 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>
          <h3 className="text-lg font-medium text-emerald-50">
            {residentName}
          </h3>
        </div>
        <span
          className={`px-2 py-1 text-xs font-semibold rounded-full ${
            localPaid
              ? "bg-emerald-400/20 text-emerald-400"
              : "bg-red-400/20 text-red-400"
          }`}
        >
          {localPaid ? "Paid" : "Unpaid"}
        </span>
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-emerald-300">Invoice #:</span>
          <span className="text-emerald-50">{invoiceNumber}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-emerald-300">Invoice Week:</span>
          <span className="text-emerald-50">{invoiceWeek}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-emerald-300">Funding Source:</span>
          <span className="text-emerald-50">{fundingSource}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-emerald-300">Amount:</span>
          <span className="text-emerald-50">${paymentAmount}</span>
        </div>
      </div>
    </div>
  );
};

const Invoices = () => {
  const navigate = useNavigate();
  const { organizationId, organizationData, checkPermission } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedFundingSource, setSelectedFundingSource] = useState("all");
  const [years, setYears] = useState([]);
  const [paidStates, setPaidStates] = useState({});
  const [fundingSources, setFundingSources] = useState(["all"]);
  const [showAllInvoices, setShowAllInvoices] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const INITIAL_DISPLAY_COUNT = 75;

  useEffect(() => {
    const loadFundingSources = async () => {
      if (!organizationId) return;

      try {
        const settingsRef = doc(
          db,
          `organizations/${organizationId}/houseSettings/initial`
        );
        const settingsSnap = await getDoc(settingsRef);

        if (settingsSnap.exists() && settingsSnap.data().fundingSources) {
          // Add "all" option to the beginning of the array
          setFundingSources(["all", ...settingsSnap.data().fundingSources]);
        }
      } catch (error) {
        console.error("Error loading funding sources:", error);
        setFundingSources(["all"]); // Fallback to just having "all" option
      }
    };

    loadFundingSources();
  }, [organizationId]);

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!organizationId) return;

      setIsLoading(true);

      try {
        // First, get all invoices to build the years dropdown
        const allInvoicesQuery = query(
          collection(db, `organizations/${organizationId}/invoices`)
        );

        const allInvoicesSnapshot = await getDocs(allInvoicesQuery);

        // Extract all unique years for the dropdown
        const uniqueYears = Array.from(
          new Set(
            allInvoicesSnapshot.docs
              .filter((doc) => doc.data().paymentDate)
              .map((doc) => doc.data().paymentDate.toDate().getFullYear())
          )
        );

        // Sort years descending (newest first)
        const sortedYears = uniqueYears.sort((a, b) => b - a);
        setYears(sortedYears);

        // If there are no years yet (empty collection) or the selected year isn't found,
        // use current year as fallback
        if (sortedYears.length === 0 || !sortedYears.includes(selectedYear)) {
          const currentYear = new Date().getFullYear();
          if (!sortedYears.includes(currentYear)) {
            setYears([currentYear]);
          }
          setSelectedYear(currentYear);
        }

        // Now query with the year filter
        const startDate = new Date(selectedYear, 0, 1);
        const endDate = new Date(selectedYear, 11, 31, 23, 59, 59);

        const invoicesQuery = query(
          collection(db, `organizations/${organizationId}/invoices`),
          where("paymentDate", ">=", startDate),
          where("paymentDate", "<=", endDate)
        );

        const invoicesSnapshot = await getDocs(invoicesQuery);

        const invoicesData = invoicesSnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Use the residentName field directly from the invoice document
            // This preserves names for discharged residents
            residentName: data.residentName || "Unknown Resident",
            paymentDate: data.paymentDate?.toDate(),
            paidInvoice: data.paidInvoice || false,
          };
        });

        // Filter by funding source if needed
        const filteredInvoices =
          selectedFundingSource === "all"
            ? invoicesData
            : invoicesData.filter(
                (invoice) => invoice.fundingSource === selectedFundingSource
              );

        // Update the paid states from the fetched data
        const newPaidStates = {};
        filteredInvoices.forEach((invoice) => {
          newPaidStates[invoice.id] = invoice.paidInvoice;
        });
        setPaidStates(newPaidStates);

        setInvoices(filteredInvoices);
      } catch (error) {
        console.error("Error fetching invoices:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoices();
  }, [selectedYear, selectedFundingSource, organizationId]);

  const handleYearChange = (event) => {
    setSelectedYear(Number(event.target.value));
  };

  const handleFundingSourceChange = (event) => {
    setSelectedFundingSource(event.target.value);
  };

  const handlePaidChange = (id, newValue) => {
    setPaidStates((prevState) => ({ ...prevState, [id]: newValue }));

    // Update the invoice in the invoices array to reflect the change
    setInvoices((prevInvoices) =>
      prevInvoices.map((invoice) =>
        invoice.id === id ? { ...invoice, paidInvoice: newValue } : invoice
      )
    );
  };

  // Sort invoices for display
  const sortedInvoices = [...invoices].sort((a, b) => {
    // First sort by paid status (unpaid first)
    const paidA =
      paidStates[a.id] !== undefined ? paidStates[a.id] : a.paidInvoice;
    const paidB =
      paidStates[b.id] !== undefined ? paidStates[b.id] : b.paidInvoice;

    if (paidA !== paidB) {
      return paidA ? 1 : -1; // Unpaid invoices first
    }

    // Then by funding source
    if (a.fundingSource !== b.fundingSource) {
      return a.fundingSource.localeCompare(b.fundingSource);
    }

    // Then by invoice number (descending)
    return parseInt(b.invoiceNumber) - parseInt(a.invoiceNumber);
  });

  const displayedInvoices = showAllInvoices
    ? sortedInvoices
    : sortedInvoices.slice(0, INITIAL_DISPLAY_COUNT);

  // Calculate total owed amount from unpaid invoices
  const totalOwed = invoices
    .filter((invoice) => {
      const isPaid =
        paidStates[invoice.id] !== undefined
          ? paidStates[invoice.id]
          : invoice.paidInvoice;
      return !isPaid;
    })
    .reduce((total, invoice) => total + invoice.paymentAmount, 0);

  return (
    <>
      <Helmet>
        <title> &bull; Invoices</title>
      </Helmet>

      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-emerald-950/30 via-zinc-900/95 to-zinc-950 p-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
        </div>

        <div className="max-w-7xl mx-auto space-y-8 relative">
          {/* Header */}
          <div className="flex items-center gap-6 mb-12">
            <div className="flex gap-3">
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white tracking-tight">
                Invoice Management
              </h1>
            </div>
          </div>

          {/* Action buttons and filters */}
          <div className="grid grid-cols-1 gap-4 mb-8">
            <div className="flex flex-col md:flex-row justify-between gap-4">
              <button
                onClick={() => navigate("/payments")}
                className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-emerald-500/25 font-medium flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Payments
              </button>

              <div className="flex flex-col md:flex-row gap-4">
                <select
                  onChange={handleYearChange}
                  value={selectedYear}
                  className="w-full md:w-auto px-6 py-3 bg-slate-800/50 backdrop-blur-xl text-white rounded-xl border border-slate-700/50 hover:border-slate-600/50 transition-all text-center"
                  style={{ minWidth: "100px" }}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>

                <select
                  onChange={handleFundingSourceChange}
                  value={selectedFundingSource}
                  className="w-full md:w-auto px-6 py-3 bg-slate-800/50 backdrop-blur-xl text-white rounded-xl border border-slate-700/50 hover:border-slate-600/50 transition-all text-center"
                  style={{ minWidth: "220px" }}
                >
                  {fundingSources.map((source) => (
                    <option key={source} value={source}>
                      {source === "all" ? "All Funding Sources" : source}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
            </div>
          ) : (
            <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Desktop View */}
              <div className="hidden md:block relative overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">
                        Paid
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">
                        Resident Name
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">
                        Invoice #
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">
                        Invoice Week
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">
                        Funding Source
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">
                        Amount
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedInvoices.map((invoice) => (
                      <InvoiceRow
                        key={invoice.id}
                        id={invoice.id}
                        residentId={invoice.residentId}
                        invoiceNumber={invoice.invoiceNumber}
                        invoiceWeek={invoice.invoiceWeek}
                        fundingSource={invoice.fundingSource}
                        paymentAmount={invoice.paymentAmount}
                        paid={
                          paidStates[invoice.id] !== undefined
                            ? paidStates[invoice.id]
                            : invoice.paidInvoice
                        }
                        onPaidChange={handlePaidChange}
                        originalPaymentRef={invoice.originalPaymentRef}
                        residentName={invoice.residentName}
                      />
                    ))}

                    <tr className="bg-slate-950">
                      <td
                        colSpan="5"
                        className="px-6 py-4 text-right font-medium text-emerald-400"
                      >
                        Total Owed:
                      </td>
                      <td
                        colSpan="2"
                        className="px-6 py-4 font-medium text-emerald-400"
                      >
                        ${totalOwed.toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {/* Mobile View */}
              <div className="md:hidden space-y-4 p-4">
                {displayedInvoices.map((invoice) => (
                  <InvoiceCard
                    key={invoice.id}
                    id={invoice.id}
                    residentId={invoice.residentId}
                    invoiceNumber={invoice.invoiceNumber}
                    invoiceWeek={invoice.invoiceWeek}
                    fundingSource={invoice.fundingSource}
                    paymentAmount={invoice.paymentAmount}
                    paid={
                      paidStates[invoice.id] !== undefined
                        ? paidStates[invoice.id]
                        : invoice.paidInvoice
                    }
                    onPaidChange={handlePaidChange}
                    originalPaymentRef={invoice.originalPaymentRef}
                    residentName={invoice.residentName}
                  />
                ))}

                {/* Total Owed Section for Mobile */}
                <div className="bg-slate-950 rounded-lg p-4 flex justify-between items-center">
                  <span className="font-medium text-emerald-400">
                    Total Owed:
                  </span>
                  <span className="font-medium text-emerald-400">
                    ${totalOwed.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Show More Button */}
              {invoices.length > INITIAL_DISPLAY_COUNT && (
                <div className="flex justify-center mt-6 p-4 relative z-[1050]">
                  <button
                    onClick={() => {
                      setShowAllInvoices(!showAllInvoices);
                    }}
                    className="bg-emerald-600/80 hover:bg-emerald-600 text-white px-6 py-2 rounded-lg transition-all duration-200"
                  >
                    {showAllInvoices
                      ? "Show Less"
                      : `Show More (${
                          invoices.length - INITIAL_DISPLAY_COUNT
                        } remaining)`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Invoices;
